/**
 * =============================================================================
 * FORMATTING UTILITIES
 * =============================================================================
 *
 * Shared formatting functions for the admin UI.
 *
 * =============================================================================
 */

/**
 * Formats a timestamp for display.
 *
 * @param timestamp - Unix timestamp (ms) or ISO date string
 * @returns Formatted date string (e.g., "Dec 3, 2024")
 *
 * @example
 * ```ts
 * formatDate(1701619200000); // "Dec 3, 2023"
 * formatDate('2024-12-03T00:00:00Z'); // "Dec 3, 2024"
 * ```
 */
export function formatDate(timestamp: number | string): string {
  const date =
    typeof timestamp === 'string' ? new Date(timestamp) : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Formats a timestamp as a relative time string.
 *
 * @param timestamp - ISO date string or unix timestamp (ms)
 * @returns Relative time string (e.g., "2 hours ago", "Yesterday")
 *
 * @example
 * ```ts
 * formatRelativeTime('2024-12-03T10:00:00Z'); // "2 hours ago"
 * formatRelativeTime(Date.now() - 60000); // "1 minute ago"
 * ```
 */
export function formatRelativeTime(timestamp: number | string): string {
  const date =
    typeof timestamp === 'string' ? new Date(timestamp) : new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return 'Just now';
  }
  if (diffMins < 60) {
    return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  }
  if (diffDays === 1) {
    return 'Yesterday';
  }
  if (diffDays < 7) {
    return `${diffDays} days ago`;
  }

  // Fall back to formatted date for older items
  return formatDate(timestamp);
}
